/**
 * Admin Ebooks Management
 * Full CRUD with file upload support
 */
import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApiGet, adminApiDelete, adminApiPatch } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowSquareOut,
  CircleNotch,
  DownloadSimple,
  Eye,
  EyeSlash,
  FileText,
  Funnel,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Trash,
  UploadSimple
} from '@phosphor-icons/react';
const EbooksManagement = () => {
  const navigate = useNavigate();
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchEbooks();
  }, []);

  const fetchEbooks = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/ebooks');
      setEbooks(data || []);
    } catch (error) {
      console.error('Error fetching ebooks:', error);
      toast.error('Failed to load ebooks');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This action cannot be undone.`)) return;

    try {
      await adminApiDelete(`/api/admin/ebooks/${id}`);
      toast.success('Ebook deleted successfully');
      fetchEbooks();
    } catch (error) {
      console.error('Error deleting ebook:', error);
      toast.error(error.message || 'Failed to delete ebook');
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      await adminApiPatch(`/api/admin/ebooks/${id}`, { published: !currentStatus });
      toast.success(currentStatus ? 'Ebook unpublished' : 'Ebook published');
      fetchEbooks();
    } catch (error) {
      console.error('Error updating ebook:', error);
      toast.error(error.message || 'Failed to update ebook');
    }
  };

  const filteredEbooks = ebooks
    .filter(ebook => {
      const matchesSearch = ebook.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' ||
                           (statusFilter === 'published' && ebook.published) ||
                           (statusFilter === 'draft' && !ebook.published);
      return matchesSearch && matchesStatus;
    });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Ebook Management</h1>
          <p className="text-gray-400">Manage downloadable ebooks and resources</p>
        </div>
        <button
          onClick={() => navigate('/admin/ebooks/new')}
          className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 font-semibold"
        >
          <Plus size={18} />
          New Ebook
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search ebooks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Drafts</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <FileText size={20} className="text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Ebooks</p>
              <p className="text-2xl font-bold text-white">{ebooks.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Eye size={20} className="text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Published</p>
              <p className="text-2xl font-bold text-white">
                {ebooks.filter(e => e.published).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <DownloadSimple size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Downloads</p>
              <p className="text-2xl font-bold text-white">
                {ebooks.reduce((sum, e) => sum + (e.downloads || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ebooks List */}
      {filteredEbooks.length === 0 ? (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
          <FileText size={48} className="text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No ebooks found' : 'No ebooks yet'}
          </h3>
          <p className="text-gray-400 mb-4">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first ebook to get started'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <button
              onClick={() => navigate('/admin/ebooks/new')}
              className="px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <Plus size={18} />
              Create First Ebook
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredEbooks.map((ebook) => (
            <motion.div
              key={ebook.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{ebook.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      ebook.published
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {ebook.published ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  {ebook.description && (
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">{ebook.description}</p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {ebook.category && (
                      <span className="px-2 py-1 bg-white/5 rounded">{ebook.category}</span>
                    )}
                    <span className="flex items-center gap-1">
                      <DownloadSimple size={14} />
                      {ebook.downloads || 0} downloads
                    </span>
                    {ebook.file_path && (
                      <span className="flex items-center gap-1">
                        <FileText size={14} />
                        PDF Available
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {ebook.file_path && (
                    <a
                      href={`${BACKEND_URL}${ebook.file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Download"
                    >
                      <DownloadSimple size={18} />
                    </a>
                  )}
                  <button
                    onClick={() => togglePublish(ebook.id, ebook.published)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title={ebook.published ? 'Unpublish' : 'Publish'}
                  >
                    {ebook.published ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={() => navigate(`/admin/ebooks/edit/${ebook.id}`)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <PencilSimple size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(ebook.id, ebook.title)}
                    className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EbooksManagement;
